import type { Event } from 'store/api/event';
import { formatShowDate } from 'utils/show-hours-parser';
import type {
  QuickBooksConnection,
  QuickBooksProjectLink,
} from 'store/api/quickbooks';

export const getLocationHeader = (isHideForSalesAndManager?: boolean) => {
  const titles = ['#', 'Size', 'Type', 'Event Type'];

  if (!isHideForSalesAndManager) {
    titles.splice(1, 0, '$');
  }

  return titles;
};

export const QUICKBOOKS_LINK_STATUS = {
  checking: 'Checking…',
  unavailable: 'Unavailable',
  notLinked: 'Not linked yet',
  blocked: 'Blocked — needs review in Control Center',
  linked: (qboProjectId: string) => `Linked · QBO #${qboProjectId}`,
} as const;

export interface QuickBooksLinkStatusInput {
  eventName?: string;
  eventId: number | string;
  isProjectsError?: boolean;
  isProjectsLoading?: boolean;
  isConnectionError?: boolean;
  isConnectionLoading?: boolean;
  connection?: Pick<QuickBooksConnection, 'status' | 'realmId'> | null;
  links?: Pick<
    QuickBooksProjectLink,
    'state' | 'eventId' | 'qboProjectId' | 'qboProjectName'
  >[];
}

export interface QuickBooksLinkStatus {
  status: string;
  projectName: string;
}

/**
 * Derives the event page's "QuickBooks Project" and "QuickBooks Status"
 * values from the connection and the company's QbProjectLinks.
 */
export const getQuickBooksLinkStatus = ({
  links,
  eventId,
  eventName,
  connection,
  isProjectsError,
  isConnectionError,
  isProjectsLoading,
  isConnectionLoading,
}: QuickBooksLinkStatusInput): QuickBooksLinkStatus => {
  const expectedName = `CLIX-${eventId} - ${eventName ?? ''}`;
  const withStatus = (status: string) => ({
    status,
    projectName: expectedName,
  });

  if (isConnectionLoading) {
    return withStatus(QUICKBOOKS_LINK_STATUS.checking);
  }

  if (
    isConnectionError ||
    connection?.status !== 'active' ||
    !connection.realmId ||
    isProjectsError
  ) {
    return withStatus(QUICKBOOKS_LINK_STATUS.unavailable);
  }

  if (isProjectsLoading || !links) {
    return withStatus(QUICKBOOKS_LINK_STATUS.checking);
  }

  const link = links.find((item) => Number(item.eventId) === Number(eventId));

  if (!link) {
    return withStatus(QUICKBOOKS_LINK_STATUS.notLinked);
  }

  return {
    projectName: link.qboProjectName || expectedName,
    status:
      link.state === 'verified'
        ? QUICKBOOKS_LINK_STATUS.linked(link.qboProjectId)
        : QUICKBOOKS_LINK_STATUS.blocked,
  };
};

// "10:00 am": the time pickers' label (formatTimeData's 'h:mm a'), with no
// day attached, so a daylight-saving day can't move it an hour.
const formatClock = (ms: number) => {
  const minutes = Math.round(ms / 60_000);
  const hours = Math.floor(minutes / 60) % 24;
  const h12 = hours % 12 === 0 ? 12 : hours % 12;
  return `${h12}:${String(minutes % 60).padStart(2, '0')} ${hours < 12 ? 'am' : 'pm'}`;
};

const capitalize = (text: string) =>
  text ? text[0].toUpperCase() + text.slice(1) : text;

/**
 * The details page's hours (no-sale alerts, 22 Oct 2026): one item per show
 * date from Events.schedule; a show with none yet shows its old weekly
 * schedule. Empty when there is neither.
 */
export const getShowHoursSection = (
  data: Pick<Event, 'schedule' | 'weeklySchedule'>
): { title: string; items: { title: string; value: string | null }[] } => {
  const schedule = Array.isArray(data.schedule) ? data.schedule : [];
  const dated = schedule
    .map((entry) => ({
      ...entry,
      date: String(entry?.date ?? '').slice(0, 10),
    }))
    .filter((entry) => /^\d{4}-\d{2}-\d{2}$/.test(entry.date))
    .sort((a, b) => a.date.localeCompare(b.date));

  if (dated.length) {
    return {
      title: 'Show hours',
      items: dated.map(({ date, time, dayOff }) => ({
        title: formatShowDate(date),
        value: dayOff
          ? 'Closed'
          : typeof time?.from === 'number' && typeof time?.to === 'number'
            ? `${formatClock(time.from)} - ${formatClock(time.to)}`
            : null,
      })),
    };
  }

  const weekly = data.weeklySchedule ?? [];
  return {
    title: 'Weekly Schedule',
    items: weekly.map(({ to, day, from }) => ({
      title: capitalize(day),
      value:
        typeof to === 'number' && typeof from === 'number'
          ? `${formatClock(from)} - ${formatClock(to)}`
          : null,
    })),
  };
};
